import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";

type VerificationStatus = "confirm" | "submitting" | "verified" | "expired" | "invalid" | "error";

const content: Record<
  VerificationStatus,
  { icon: string; iconClass: string; title: string; message: string }
> = {
  confirm: {
    icon: "fa-envelope-circle-check",
    iconClass: "text-brand-red",
    title: "Confirm this sales destination",
    message:
      "Confirm that this address is authorised to receive lead information. Opening the email link alone does not activate delivery.",
  },
  submitting: {
    icon: "fa-spinner fa-spin",
    iconClass: "text-brand-red",
    title: "Confirming this sales destination",
    message: "Please wait while FMC Leads Manager verifies this recipient.",
  },
  verified: {
    icon: "fa-circle-check",
    iconClass: "text-status-green",
    title: "Sales destination verified",
    message: "This address is now authorised to receive lead notifications from FMC Leads Manager.",
  },
  expired: {
    icon: "fa-clock-rotate-left",
    iconClass: "text-status-amber",
    title: "This verification link has expired",
    message:
      "A new verification email has been sent automatically. Please use the latest email to verify this sales destination; its link will be valid for 24 hours.",
  },
  invalid: {
    icon: "fa-circle-exclamation",
    iconClass: "text-status-red",
    title: "This verification link cannot be used",
    message:
      "The link is invalid, has already been used, or has been replaced. Please use the most recent verification email.",
  },
  error: {
    icon: "fa-circle-exclamation",
    iconClass: "text-status-red",
    title: "We could not verify this destination",
    message:
      "The verification service could not complete the request. Please try again. Your destination has not been activated.",
  },
};

export function DestinationVerification() {
  const [searchParams] = useSearchParams();
  const [token] = useState(
    () => new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token") ?? "",
  );
  const [status, setStatus] = useState<VerificationStatus>(() => {
    if (token) return "confirm";
    const rawStatus = searchParams.get("status");
    return rawStatus === "verified" || rawStatus === "expired" ? rawStatus : "invalid";
  });
  const state = content[status];

  async function confirmDestination() {
    if (!token || status === "submitting") return;
    setStatus("submitting");
    try {
      const response = await fetch("/api/destinations/verify", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const result = (await response.json().catch(() => null)) as { status?: unknown } | null;
      if (!response.ok) throw new Error("verification_request_failed");
      if (
        result?.status !== "verified" &&
        result?.status !== "expired" &&
        result?.status !== "invalid"
      ) {
        throw new Error("verification_response_invalid");
      }
      window.history.replaceState(window.history.state, "", window.location.pathname);
      setStatus(result.status);
    } catch {
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-surface-light flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[460px]">
        <div className="flex justify-center mb-8">
          <Brand logoHeight={62} align="center" />
        </div>

        <div className="bg-surface-white border border-default rounded-xl px-[34px] py-[34px] text-center">
          <span className="w-12 h-12 rounded-full bg-surface-light border border-default inline-flex items-center justify-center mb-5">
            <i className={`fa-solid ${state.icon} text-[21px] ${state.iconClass}`} />
          </span>
          <h1 className="text-title-md font-bold text-primary mb-2">{state.title}</h1>
          <p className="text-body text-secondary leading-6">{state.message}</p>

          {status === "confirm" || status === "error" ? (
            <button
              type="button"
              onClick={() => void confirmDestination()}
              className="h-11 mt-7 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors inline-flex items-center justify-center"
            >
              Confirm sales destination
            </button>
          ) : status === "submitting" ? (
            <button
              type="button"
              disabled
              className="h-11 mt-7 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold opacity-70 inline-flex items-center justify-center"
            >
              Confirming…
            </button>
          ) : (
            <Link
              to="/login"
              className="h-11 mt-7 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors inline-flex items-center justify-center"
            >
              Return to FMC Leads Manager
            </Link>
          )}
        </div>

        {status !== "confirm" && status !== "submitting" && status !== "error" ? (
          <p className="text-caption text-tertiary text-center mt-6">
            You can safely close this window.
          </p>
        ) : null}
      </div>
    </div>
  );
}
